import type { IslandShortcut, MediaAccess, RecordingPermissions } from "./index";

/**
 * The desktop shell's recording rules that its main process and the web app share. Kept apart from
 * the index, which carries zod schemas, so the shell's main process bundles only these.
 */

const allowed = (access: MediaAccess) => access === "granted" || access === "unknown";

/**
 * Whether a recording can be shown on this platform (`DesktopBridge.platform`): the screen, the
 * mic and Accessibility are all allowed, or there is nothing to ask. A shell that does not report
 * Accessibility has nothing to wait for there. Windows asks a desktop app nothing before it first
 * uses the mic, so a mic it has not decided on holds nothing there; one it was told to refuse
 * (`denied`, `restricted`) does.
 */
export function isRecordingReady(permissions: RecordingPermissions, platform: string): boolean {
  const microphone =
    allowed(permissions.microphone) ||
    (platform === "win32" && permissions.microphone === "not-asked");
  return (
    allowed(permissions.screen) && microphone && allowed(permissions.accessibility ?? "unknown")
  );
}

/**
 * The island's global shortcuts on a platform, as key names in Electron's accelerator order. The
 * shell joins them with `+` to hold them; the island draws them. Alt+Space opens the active
 * window's system menu on Windows, so there Ctrl joins it: Microsoft reserves the Windows logo
 * key, and Ctrl+Shift+Space already means something in Word, Excel and code editors. The mic's
 * (bridge version 9) is M for mic, with the same modifiers as typing.
 */
export function islandShortcutKeys(platform: string): Record<IslandShortcut, readonly string[]> {
  return platform === "win32"
    ? {
        type: ["Ctrl", "Alt", "Space"],
        pause: ["Ctrl", "Alt", "Shift", "Space"],
        mic: ["Ctrl", "Alt", "M"],
      }
    : { type: ["Alt", "Space"], pause: ["Alt", "Shift", "Space"], mic: ["Alt", "M"] };
}
